import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../services/supabaseClient';
import axios from 'axios';
import { Typography, Grid, Paper, Box, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, MenuItem, Select, FormControl, InputLabel, CircularProgress, Card, CardContent, Alert, TableSortLabel } from '@mui/material';
import { format, parseISO } from 'date-fns';

const AdminDashboard = () => {
  // State for usage statistics
  const [totalWords, setTotalWords] = useState(0);
  const [todayWords, setTodayWords] = useState(0);
  const [totalActors, setTotalActors] = useState(0);
  const [topLanguages, setTopLanguages] = useState([]);
  const [recentTranslations, setRecentTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sourceLanguage, setSourceLanguage] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [minWords, setMinWords] = useState('');

  // Sort state for recent translations
  const [rtSortCol, setRtSortCol] = useState('created_at');
  const [rtSortDir, setRtSortDir] = useState('desc');

  // Sort state for top languages
  const [tlSortCol, setTlSortCol] = useState('words');
  const [tlSortDir, setTlSortDir] = useState('desc');
  
  // Language options
  const languageOptions = [
    { code: '', name: 'All Languages' },
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
  ];

  const fetchUsageStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        throw new Error('You must be logged in as an admin to view this dashboard');
      }

      const params = {
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        sourceLanguage: sourceLanguage || undefined,
        targetLanguage: targetLanguage || undefined,
        page,
        pageSize: rowsPerPage
      };

      const response = await axios.get('/api/admin/metrics', {
        params,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const api = response?.data;
      const summary = api?.summary;

      setTotalWords(Number(summary?.totalWords || 0));
      setTodayWords(Number(summary?.todayWords || 0));
      setTotalActors(Number(summary?.totalActors || 0));

      setTopLanguages((api?.topLanguages || []).map((l) => ({
        lang: l.lang,
        words: Number(l.words || 0),
        characters: Number(l.characters || 0),
        requestCount: Number(l.requestCount || 0)
      })));

      setRecentTranslations(api?.recent || []);
      
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [endDate, page, rowsPerPage, sourceLanguage, startDate, targetLanguage]);

  useEffect(() => {
    // Check if user is authenticated and has admin role
    const checkAdminStatus = async () => {
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        
        if (authError) {
          console.error('Error checking auth status:', authError);
          setError(`Authentication error: ${authError.message}`);
          return;
        }
        
        if (data && data.user) {
          console.log('Current user:', data.user);
        } else {
          setError('You must be logged in as an admin to view this dashboard');
        }
      } catch (err) {
        console.error('Error in admin check:', err);
        setError(`Admin check error: ${err.message}`);
      } finally {
        fetchUsageStats();
      }
    };
    
    checkAdminStatus();
    
    // Refresh stats every 5 minutes
    const interval = setInterval(fetchUsageStats, 300000);
    return () => clearInterval(interval);
  }, [fetchUsageStats]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterApply = () => {
    fetchUsageStats();
  };

  const handleFilterReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSourceLanguage('');
    setTargetLanguage('');
    setTierFilter('');
    setMinWords('');
  };

  const handleRtSort = (col) => {
    setRtSortDir(rtSortCol === col && rtSortDir === 'asc' ? 'desc' : 'asc');
    setRtSortCol(col);
  };

  const handleTlSort = (col) => {
    setTlSortDir(tlSortCol === col && tlSortDir === 'asc' ? 'desc' : 'asc');
    setTlSortCol(col);
  };

  const filteredTranslations = useMemo(() => {
    let rows = [...recentTranslations];
    if (tierFilter) rows = rows.filter((r) => r.tier === tierFilter);
    if (minWords && !isNaN(Number(minWords))) rows = rows.filter((r) => r.words >= Number(minWords));
    const dir = rtSortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = a[rtSortCol], bv = b[rtSortCol];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [recentTranslations, tierFilter, minWords, rtSortCol, rtSortDir]);

  const sortedLanguages = useMemo(() => {
    const rows = [...topLanguages];
    const dir = tlSortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let av = a[tlSortCol], bv = b[tlSortCol];
      if (tlSortCol === 'avgWords') {
        av = a.requestCount ? a.words / a.requestCount : 0;
        bv = b.requestCount ? b.words / b.requestCount : 0;
      }
      if (typeof av === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [topLanguages, tlSortCol, tlSortDir]);

  const getLanguageName = (code) => {
    const language = languageOptions.find(lang => lang.code === code);
    return language ? language.name : code;
  };

  if (loading && !recentTranslations.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Admin Dashboard
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
              onChange={(e) => {
                if (e.target.value) {
                  setStartDate(parseISO(e.target.value));
                } else {
                  setStartDate(null);
                }
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="End Date"
              type="date"
              value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
              onChange={(e) => {
                if (e.target.value) {
                  setEndDate(parseISO(e.target.value));
                } else {
                  setEndDate(null);
                }
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Source Language</InputLabel>
              <Select
                value={sourceLanguage}
                label="Source Language"
                onChange={(e) => setSourceLanguage(e.target.value)}
              >
                {languageOptions.map((lang) => (
                  <MenuItem key={`source-${lang.code}`} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Target Language</InputLabel>
              <Select
                value={targetLanguage}
                label="Target Language"
                onChange={(e) => setTargetLanguage(e.target.value)}
              >
                {languageOptions.map((lang) => (
                  <MenuItem key={`target-${lang.code}`} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tier</InputLabel>
              <Select
                value={tierFilter}
                label="Tier"
                onChange={(e) => setTierFilter(e.target.value)}
              >
                <MenuItem value="">All Tiers</MenuItem>
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Min Words"
              type="number"
              size="small"
              value={minWords}
              onChange={(e) => setMinWords(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleFilterApply}
                fullWidth
              >
                Apply
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleFilterReset}
                fullWidth
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Words Processed
              </Typography>
              <Typography variant="h4">
                {totalWords.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Today's Words
              </Typography>
              <Typography variant="h4">
                {todayWords.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Actors
              </Typography>
              <Typography variant="h4">
                {totalActors.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg. Words Per Actor
              </Typography>
              <Typography variant="h4">
                {totalActors ? Math.round(totalWords / totalActors).toLocaleString() : '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Top Languages */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Top Target Languages
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={tlSortCol === 'lang' ? tlSortDir : false}>
                  <TableSortLabel active={tlSortCol === 'lang'} direction={tlSortCol === 'lang' ? tlSortDir : 'asc'} onClick={() => handleTlSort('lang')}>Language</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={tlSortCol === 'words' ? tlSortDir : false}>
                  <TableSortLabel active={tlSortCol === 'words'} direction={tlSortCol === 'words' ? tlSortDir : 'asc'} onClick={() => handleTlSort('words')}>Words</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={tlSortCol === 'characters' ? tlSortDir : false}>
                  <TableSortLabel active={tlSortCol === 'characters'} direction={tlSortCol === 'characters' ? tlSortDir : 'asc'} onClick={() => handleTlSort('characters')}>Characters</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={tlSortCol === 'requestCount' ? tlSortDir : false}>
                  <TableSortLabel active={tlSortCol === 'requestCount'} direction={tlSortCol === 'requestCount' ? tlSortDir : 'asc'} onClick={() => handleTlSort('requestCount')}>Requests</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={tlSortCol === 'avgWords' ? tlSortDir : false}>
                  <TableSortLabel active={tlSortCol === 'avgWords'} direction={tlSortCol === 'avgWords' ? tlSortDir : 'asc'} onClick={() => handleTlSort('avgWords')}>Avg Words/Req</TableSortLabel>
                </TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLanguages.map((lang) => (
                <TableRow key={lang.lang}>
                  <TableCell>{getLanguageName(lang.lang)}</TableCell>
                  <TableCell align="right">{lang.words.toLocaleString()}</TableCell>
                  <TableCell align="right">{lang.characters.toLocaleString()}</TableCell>
                  <TableCell align="right">{lang.requestCount.toLocaleString()}</TableCell>
                  <TableCell align="right">{lang.requestCount ? Math.round(lang.words / lang.requestCount).toLocaleString() : '0'}</TableCell>
                  <TableCell align="right">
                    {totalWords ? `${Math.round((lang.words / totalWords) * 100)}%` : '0%'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Recent Translations */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Recent Translations
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={rtSortCol === 'actor_key' ? rtSortDir : false}>
                  <TableSortLabel active={rtSortCol === 'actor_key'} direction={rtSortCol === 'actor_key' ? rtSortDir : 'asc'} onClick={() => handleRtSort('actor_key')}>Actor</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={rtSortCol === 'email' ? rtSortDir : false}>
                  <TableSortLabel active={rtSortCol === 'email'} direction={rtSortCol === 'email' ? rtSortDir : 'asc'} onClick={() => handleRtSort('email')}>Email</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={rtSortCol === 'tier' ? rtSortDir : false}>
                  <TableSortLabel active={rtSortCol === 'tier'} direction={rtSortCol === 'tier' ? rtSortDir : 'asc'} onClick={() => handleRtSort('tier')}>Tier</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={rtSortCol === 'words' ? rtSortDir : false}>
                  <TableSortLabel active={rtSortCol === 'words'} direction={rtSortCol === 'words' ? rtSortDir : 'asc'} onClick={() => handleRtSort('words')}>Words</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={rtSortCol === 'characters' ? rtSortDir : false}>
                  <TableSortLabel active={rtSortCol === 'characters'} direction={rtSortCol === 'characters' ? rtSortDir : 'asc'} onClick={() => handleRtSort('characters')}>Characters</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={rtSortCol === 'source_language' ? rtSortDir : false}>
                  <TableSortLabel active={rtSortCol === 'source_language'} direction={rtSortCol === 'source_language' ? rtSortDir : 'asc'} onClick={() => handleRtSort('source_language')}>Source</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={rtSortCol === 'target_language' ? rtSortDir : false}>
                  <TableSortLabel active={rtSortCol === 'target_language'} direction={rtSortCol === 'target_language' ? rtSortDir : 'asc'} onClick={() => handleRtSort('target_language')}>Target</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={rtSortCol === 'created_at' ? rtSortDir : false}>
                  <TableSortLabel active={rtSortCol === 'created_at'} direction={rtSortCol === 'created_at' ? rtSortDir : 'asc'} onClick={() => handleRtSort('created_at')}>Date</TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTranslations
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{String(row.actor_key || '').substring(0, 12)}...</TableCell>
                    <TableCell>{row.email || <Typography variant="caption" color="text.secondary">Anonymous</Typography>}</TableCell>
                    <TableCell>{row.tier}</TableCell>
                    <TableCell>{row.words?.toLocaleString()}</TableCell>
                    <TableCell>{row.characters?.toLocaleString()}</TableCell>
                    <TableCell>{getLanguageName(row.source_language)}</TableCell>
                    <TableCell>{getLanguageName(row.target_language)}</TableCell>
                    <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredTranslations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
